import { PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  Breadcrumb,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
  DatePicker,
} from "antd";
import { useEffect, useState } from "react";
import { getOrderRequest } from "../../../api/requests/order";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import WelcomeTopbar from "../../../components/common/header/WelcomeTopbar";
import LogoLoader from "../../../components/common/LogoLoader";
import { DATE_FORMAT } from "../../../utils/date";
import { useCurrentUser } from "../../../api/hooks/auth";
import { api } from "../../../api";
import { useContacts } from "../../../api/hooks/customer";
import {
  getOrderStatusTagColor,
  getOrderTypeTagColor,
  ORDER_STATUS,
} from "../../../constants";
import { useProducts } from "../../../api/hooks/product";

const { RangePicker } = DatePicker;

export async function downloadOrderPdf({ orderId }) {
  const res = await api.get(`/v1/order/${orderId}/pdf`);
  const pdf_data = res?.data?.data;
  // console.log("pdf_data----->", pdf_data);
  const link = document.createElement("a");
  link.href = pdf_data?.url;
  link.target = "_blank";
  link.setAttribute("download", pdf_data?.fileName);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
function OrderPage() {
  const navigate = useNavigate();
  const { data: userData } = useCurrentUser();
  const [downLoadingId, setDownLoadingId] = useState();
  const [referenceId, setReferenceId] = useState("");
  const [poNumber, setPoNumber] = useState("");

  const [params, setParams] = useState({});

  const { data: orderListRes, isLoading: isLoadingOrderList } = useQuery({
    queryKey: ["order", params],
    queryFn: async () => {
      const res = await getOrderRequest({
        params,
      });
      return res.data;
    },
  });

  const { data: carrierList, isLoading: isLoadingCarrierList } = useContacts({
    params: {
      type: "CARRIER",
    },
  });

  const { data: customerList, isLoading: isLoadingCustomerList } = useContacts({
    params: {
      type: ["CUSTOMER", "PROCESSOR"],
    },
  });

  const { data: productList, isLoading: isLoadingProductList } = useProducts({
    params: {},
  });

  function navigateToUpdate(id) {
    navigate(`/processes/order/update/${id}`);
  }

  const columns = [
    {
      title: "ID",
      key: "referenceId",
      render: ({ referenceId, _id: orderId }) => {
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <Button
              onClick={async () => {
                setDownLoadingId(orderId);
                await downloadOrderPdf({ orderId });
                setDownLoadingId(undefined);
              }}
              type="primary"
              icon={<PrinterOutlined />}
              loading={downLoadingId === orderId}
              className="flex-shrink-0"
            ></Button>
            {referenceId}
          </div>
        );
      },
    },
    {
      title: "Order Type",
      dataIndex: "type",
      key: "type",
      render: (type) => {
        return <Tag color={getOrderTypeTagColor(type)}>{type}</Tag>;
      },
    },
    {
      title: "Status",
      key: "state",
      render: ({ status }) => {
        return <Tag color={getOrderStatusTagColor(status)}>{status}</Tag>;
      },
    },
    {
      title: "PO Number",
      dataIndex: "poNumber",
      key: "poNumber",
      render: (text) => text || "-",
    },
    {
      title: "Customer",
      dataIndex: ["customer", "name"],
      key: "customer.name",
      render: (text) => text ?? "-",
    },
    {
      title: "Carrier",
      dataIndex: ["carrier", "name"],
      key: "carrier.name",
      render: (text) => text ?? "-",
    },
    {
      title: "Created By",
      dataIndex: ["createdBy", "name"],
      key: "createdBy.name",
      render: (text) => text ?? "-",
    },
    // {
    //   title: "Total Quantity",
    //   key: "total-quantity",
    //   render: ({ items = [] }) => {
    //     return items.reduce((acc, prd) => {
    //       return acc + (prd.requiredQuantity || 0);
    //     }, 0);
    //   },
    // },
    {
      title: "Ordered",
      dataIndex: "totalRequiredQuantity",
      key: "totalRequiredQuantity",
      render: (text) => text ?? "-",
    },
    {
      title: "Shipped",
      dataIndex: "totalShippedQuantity",
      key: "totalShippedQuantity",
      render: (text) => text ?? "-",
    },
    // {
    //   title: "Processing",
    //   key: "processing",
    //   render: ({ totalProcessorPingQuantity, totalReturnedQuantity }) => {
    //     return (totalProcessorPingQuantity || 0) - (totalReturnedQuantity || 0);
    //   },
    // },
    // {
    //   title: "Returned",
    //   dataIndex: "totalReturnedQuantity",
    //   key: "totalReturnedQuantity",
    //   render: (text) => text ?? "-",
    // },
    {
      title: "Delivery Date",
      key: "requiredDate",
      render: ({ requiredDate }) => {
        return dayjs(requiredDate).format(DATE_FORMAT);
      },
    },
    {
      title: "Ship Date",
      key: "shipDate",
      render: ({ shipDate }) => {
        return shipDate ? dayjs(shipDate).format(DATE_FORMAT) : "-";
      },
    },
    {
      title: "Action",
      render: (details) => {
        const { _id: orderId, status, type: order_type } = details;
        const hasAdminPrivileges = [
          "admin",
          "OrderCreateAll",
          "OrderManagement",
        ].some((p) => userData?.type?.permissions?.[p]);
        const isProcessor =
          userData?.type?.name === "PROCESSOR" ||
          userData?.type?.permissions?.admin;

        return (
          <Space size="small">
            <Button
              onClick={() => navigateToUpdate(orderId)}
              variant="solid"
              color="blue"
            >
              View
            </Button>

            {status === "INITIATED" && hasAdminPrivileges && (
              <Button
                onClick={() => navigateToUpdate(orderId)}
                variant="solid"
                color="green"
              >
                Approve
              </Button>
            )}

            {status === "APPROVED" && hasAdminPrivileges && (
              <Button
                onClick={() => navigateToUpdate(orderId)}
                variant="solid"
                color="orange"
              >
                Revoke
              </Button>
            )}

            {isProcessor &&
              order_type === "INBOUND" &&
              (status === "SHIPPED-PARTIAL" || status === "APPROVED") && (
                <Button
                  onClick={() =>
                    navigate(`/processes/shipments/inbound/add/${orderId}`)
                  }
                  variant="solid"
                  color="purple"
                >
                  Ship
                </Button>
              )}

            {/* we should have “Update” button on the webUI for OUTBOUND orders only when the status is “APPROVED”.  */}
            {/* no need of the UPDATE button */}
            {/* {order_type === "OUTBOUND" && status === "APPROVED" && (
              <Button
                onClick={() => navigateToUpdate(orderId)}
                variant="solid"
                color="purple"
              >
                Update
              </Button>
            )} */}
          </Space>
        );
      },
      key: "action",
    },
  ];

  function renderTable() {
    if (isLoadingOrderList) {
      return <LogoLoader />;
    }

    const expandedRowRender = (order) => {
      const productColumns = [
        // {
        //   title: "Product ID",
        //   dataIndex: ["product"],
        //   key: "product",
        // },
        {
          title: "Name",
          dataIndex: ["product", "name"],
          key: "product.name",
          render: (text) => text ?? "-",
        },
        {
          title: "Ordered Quantity",
          dataIndex: "requiredQuantity",
          key: "requiredQuantity",
        },
      ];

      return (
        <Table
          columns={productColumns}
          dataSource={order.items}
          rowKey="_id"
          pagination={false}
          className="pr-10"
        />
      );
    };

    return (
      <Table
        className="px-6"
        dataSource={orderListRes?.data || []}
        columns={columns}
        rowKey={"_id"}
        expandable={{
          expandedRowRender,
        }}
        pagination={{
          current: params.page,
          pageSize: params.limit,
          total: orderListRes?.count || 0,
          onChange: (page, pageSize) => {
            setParams((prev) => ({
              ...prev,
              page,
              limit: pageSize,
            }));
          },
        }}
        scroll={{
          x: true,
        }}
      />
    );
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setParams((prev) => ({ ...prev, referenceId }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [referenceId]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setParams((prev) => ({ ...prev, poNumber }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [poNumber]);

  return (
    <div className="flex flex-col">
      <WelcomeTopbar />
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6 pb-6">
          <div className="flex min-h-14 justify-between rounded-t-xl bg-[#D1DFFF] px-6 py-3">
            <Breadcrumb
              items={[
                {
                  title: <div className="text-lg">Orders</div>,
                },
              ]}
            />
            <div>
              <Button
                onClick={() => {
                  navigate("/processes/order/add");
                }}
                type="primary"
              >
                Create Order
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-6 px-6">
            <div className="flex flex-wrap gap-3">
              <Input
                value={referenceId}
                onChange={(e) => {
                  setReferenceId(e.target.value);
                }}
                className="w-40 min-w-40"
                placeholder="Search Order Id"
              ></Input>
              <Input
                value={poNumber}
                onChange={(e) => {
                  setPoNumber(e.target.value);
                }}
                className="w-40 min-w-40"
                placeholder="Search PO Number"
              ></Input>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Carrier"
                allowClear
                loading={isLoadingCarrierList}
                options={carrierList?.map(({ name, _id }) => ({
                  label: name,
                  value: _id,
                }))}
                value={params?.carrier}
                onChange={(carrier) => {
                  setParams((prev) => ({ ...prev, carrier }));
                }}
                className="min-w-40"
              />

              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Status"
                allowClear
                loading={isLoadingCarrierList}
                options={ORDER_STATUS?.map((status) => {
                  return {
                    label: status,
                    value: status,
                  };
                })}
                value={params?.status}
                onChange={(status) => {
                  setParams((prev) => ({ ...prev, status }));
                }}
                className="min-w-40"
              />
              <Select
                // showSearch
                // optionFilterProp="label"
                placeholder="Order Type"
                allowClear
                options={[
                  {
                    label: "OUTBOUND",
                    value: "OUTBOUND",
                  },
                  {
                    label: "INBOUND",
                    value: "INBOUND",
                  },
                ]}
                value={params?.type}
                onChange={(type) => {
                  setParams((prev) => ({ ...prev, type }));
                }}
                className="min-w-40"
              />

              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Product"
                allowClear
                loading={isLoadingProductList}
                options={productList?.map(({ name, _id }) => {
                  return {
                    label: name,
                    value: _id,
                  };
                })}
                className="min-w-40"
                value={params?.product}
                onChange={(product) => {
                  setParams((prev) => ({ ...prev, product }));
                }}
              />

              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Customer"
                allowClear
                loading={isLoadingCustomerList}
                options={customerList?.map(({ name, _id }) => ({
                  label: name,
                  value: _id,
                }))}
                className="min-w-40"
                value={params?.customer}
                onChange={(customer) => {
                  setParams((prev) => ({ ...prev, customer }));
                }}
              />

              <RangePicker
                value={[params.createdAtFrom, params.createdAtTo]}
                onChange={([createdAtFrom, createdAtTo]) => {
                  setParams((prev) => ({
                    ...prev,
                    createdAtFrom,
                    createdAtTo,
                  }));
                }}
              />
            </div>
          </div>

          {renderTable()}
        </div>
      </div>
    </div>
  );
}

export default OrderPage;
